import React, { useCallback, useEffect, useState } from 'react';

import { Formik, Form } from 'formik';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { genericRequest } from 'app/api';
import { getSelectDefaultValues, Select } from 'app/components/Select';
import Button from 'app/components/Button';
import Checkbox from 'app/components/Checkbox';
import Spinner from 'app/components/Spinner';

import posthog from 'posthog-js';
import styles from './styles.module.scss';
import { getHoursData } from '../../utils/getData';
import { validationSchema } from '../../utils/form';

export default function ConfigurationForm() {
  const { user } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const intl = useIntl();
  const selectOptions = getHoursData(intl);

  const fetchData = useCallback(
    async (controller) => {
      const confirmationDelay = await genericRequest({
        url: `order-confirmation-delay/${user.id}`,
        signal: controller.signal
      });
      setInitialValues({
        delayOrder: confirmationDelay?.data?.attributes?.hoursDelay !== null,
        hoursDelay: confirmationDelay?.data?.attributes?.hoursDelay
      });
      setDataLoaded(true);
    },
    [user.id]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchData(controller);

    return () => controller.abort();
  }, [fetchData]);

  const handleOnSubmit = async (values) => {
    setLoading(true);
    try {
      await genericRequest({
        url: `order-confirmation-delay/${user.id}`,
        method: 'POST',
        data: {
          data: {
            type: 'order-confirmation-delay',
            id: user.id,
            attributes: {
              hoursDelay: values.delayOrder ? values.hoursDelay : null
            }
          }
        },
        customSuccessFeedback: {
          variant: 'success',
          content: intl.formatMessage({
            id: 'controlpanel.feedback.save_changes.success'
          }),
          autoClose: true,
          dismissible: false
        }
      });
      posthog.capture('order_shipping_delay_configured', {
        active: values.delayOrder ? 'si' : 'no',
        delay_time_selected: values.hoursDelay
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.configurationForm}>
      {dataLoaded ? (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(intl)}
          onSubmit={handleOnSubmit}
        >
          {(formik) => (
            <Form className={styles.form}>
              {!loading ? (
                <>
                  <Checkbox
                    id="delayOrder"
                    name="delayOrder"
                    type="checkbox"
                    label={intl.formatMessage({
                      id: 'controlpanel.orders.configuration.form.label_delay'
                    })}
                    checked={formik?.values?.delayOrder}
                  />
                  <div className={styles.selectContainer}>
                    <Select
                      disabled={!formik.values.delayOrder}
                      id="hoursDelay"
                      name="hoursDelay"
                      label={intl.formatMessage({
                        id: 'controlpanel.orders.configuration.form.label_hours'
                      })}
                      placeholder={intl.formatMessage({
                        id: 'controlpanel.global.hours'
                      })}
                      options={selectOptions}
                      defaultValue={getSelectDefaultValues(selectOptions, [
                        formik.values?.hoursDelay
                      ])}
                    />
                  </div>

                  <div className={styles.buttonContainer}>
                    <Button
                      text={intl.formatMessage({
                        id: 'controlpanel.global.save'
                      })}
                      type="secondary"
                      size="big"
                      onClick={formik.submitForm}
                      disabled={!formik.dirty}
                    />
                  </div>
                </>
              ) : (
                <Spinner />
              )}
            </Form>
          )}
        </Formik>
      ) : (
        <Spinner />
      )}
    </div>
  );
}
